import axios from 'axios';
import api from 'services/api';
import { received, type Received } from 'validation';

import { readResponse, type ResponseContract } from './readResponse';

/**
 * A 404 is an **answer**, not a failed request: "there is no record 5" is a
 * legitimate reply to "fetch record 5". It is the one status that crosses from
 * the error channel into the state channel here. Everything else — a 5xx, a
 * dropped connection, an expired session — means no answer arrived, so it stays
 * a rejection and carries the server's own complaint to whoever asked.
 */
const isNotFound = (err: unknown): boolean => axios.isAxiosError(err) && err.response?.status === 404;

/**
 * GET one record and say what came back (issue #1035).
 *
 * Shared because the answer to "what does a by-id read do when the response is
 * wrong?" must be given **once**. Seven endpoints each held their own copy of
 * this function, and all seven had inherited the same wrong answer — a bare
 * `catch { return null }` that made a 5xx, a dropped connection and a drifted
 * body indistinguishable from a deleted record, so every one of them told the
 * operator that the UI was stale. Refreshing fixes exactly one of those four —
 * and these reads exist precisely so a dialog can be opened from the Workspace,
 * where the list the old message blamed does not exist at all.
 *
 * The outcomes come back as the `Received` arms plus a nullable payload:
 *
 * - `ok` with a record — the record, with no reservations.
 * - `ok` with `null` — the server answered, and the answer is that there is no
 *   such record (404).
 * - `degraded` — readable, but something in it had to be substituted or
 *   dropped, and `problems` says what.
 * - `unreadable` — the server answered and we could not read it: the body was
 *   structurally wrong, or a field with no substitute had drifted. There is no
 *   value on this arm, so it cannot be opened on by accident.
 *
 * Anything else rejects.
 */
export const getRecord = async <T>(path: string, contract: ResponseContract<T>): Promise<Received<T | null>> => {
  try {
    const res = await api.get(path);
    return readResponse(res.data, contract);
  } catch (err) {
    if (isNotFound(err)) return received(null);
    throw err;
  }
};
